// /src/engine/SceneManager.ts

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type {
  EditorMode,
  PendingPlacement,
  PlacedPart,
  RenderMode,
  SnapSettings,
  SubmodelInstance,
} from '@/store/modelStore';
import { MAIN_SUBMODEL_ID } from '@/store/modelStore';
import { PartLibrary } from './partLibrary';
import { LDRAW_BASE, LDRAW_SHADOW_BASE } from './ldrawConfig';
import { applySnapToObject, snapValue } from './snap';
import {
  applyConnectorSnap,
  applyHingeAxisConstraint,
  findBestConnectorMatch,
} from './connectorSnap';
import { evaluateCollision } from './collision';
import { getWorldCoreBoxForObject, sampleCoreTopYAtXZ } from './coreBounds';
import {
  ApplyBrickPose,
  SolveDragPlacement,
  type DragSolveResult,
  type DragSolveState,
} from './dragPlacement';

export type SceneManagerCallbacks = {
  onSelect: (id: string | null) => void;
  onMatrixChange: (id: string, m: number[], commit: boolean) => void;
  getMode: () => EditorMode;
  getSnap: () => SnapSettings;
  getUiMode: () => 'edit' | 'instructions';
  onCollision: (c: 'ok' | 'warn' | 'bad') => void;
  getSubmodelLabel: (submodelId: string) => string;
  onPlacementCommit: (point: { x: number; y: number; z: number }) => void;
};

export class SceneManager {
  private host: HTMLElement;
  private callbacks: SceneManagerCallbacks;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private transform: TransformControls;
  private transformHelper!: THREE.Object3D;
  private composer: EffectComposer;
  private ssao: SSAOPass;
  private outline: OutlinePass;
  private bloom: UnrealBloomPass;
  private smaa: SMAAPass;
  private theme: 'dark' | 'light' = 'dark';
  private hemi!: THREE.HemisphereLight;
  private dir!: THREE.DirectionalLight;
  private grid!: THREE.GridHelper;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private partsRoot = new THREE.Group();
  private floor!: THREE.Mesh;
  // Selectable objects in the *current context* (direct children in the active submodel).
  private partMap = new Map<string, THREE.Object3D>(); // (kept name for minimal changes)
  // All rendered leaf part objects (including inside instances) for snapping/collision.
  private leafParts: THREE.Object3D[] = [];
  // Render graph node cache keyed by "path keys" so instances can duplicate submodels safely.
  private renderMap = new Map<string, THREE.Object3D>();
  private createPromises = new Map<string, Promise<THREE.Object3D>>();

  private lastParts: PlacedPart[] = [];
  private lastInstances: SubmodelInstance[] = [];
  private lib = new PartLibrary({
    partsLibraryPath: LDRAW_BASE,
    shadowLibraryPath: LDRAW_SHADOW_BASE,
  });
  private disposeFns: Array<() => void> = [];
  private lastCollisionCheck = 0;
  private renderMode: RenderMode = 'edit';
  private uiMode: 'edit' | 'instructions' = 'edit';
  private viewStep = 999;
  private lastPlacePoint: THREE.Vector3 | null = null;
  private lastPlaceTime = 0;
  private readonly PLACE_POINT_TTL_MS = 2000; // how long a hover point stays valid for sidebar clicks
  // True while the pointer is inside the WebGL canvas (prevents UI clicks using stale hover points).
  private pointerInCanvas = false;
  private placementDraft: PendingPlacement | null = null;
  private placementDraftSerial = 0;
  private placementPreview: THREE.Object3D | null = null;
  private placementOffsetY = 0;
  private placementOffsetPromise: Promise<number> | null = null;
  private freeDrag:
    | {
        pointerId: number;
        id: string;
        obj: THREE.Object3D;
        others: THREE.Object3D[];
        startX: number;
        startY: number;
        moved: boolean;
        bottomOffsetY: number;
      }
    | null = null;
  private readonly FREE_DRAG_THRESHOLD_PX = 3;
  private dragOthers: THREE.Object3D[] | null = null;
  private dragSolveState: DragSolveState | null = null;
  private dragDebugPoints: THREE.Points | null = null;
  private dragDebugPairLine: THREE.Line | null = null;
  private dragGhostBoundsHelper: THREE.Box3Helper | null = null;
  private dragGhostBoundsBox = new THREE.Box3();
  private dragDebugPointsMat: THREE.PointsMaterial | null = null;
  private dragDebugLineMat: THREE.LineBasicMaterial | null = null;
  private lastSnapDebugSignature: string | null = null;
  private dragTranslateUsedSmartSolve = false;
  // "Context" submodel id. null means MAIN_SUBMODEL_ID.
  private submodelFilter: string | null = null;
  private animationHandle = 0;
  // Instance selection visualization (AABB + HTML tag)
  private overlayEl!: HTMLDivElement;
  private instanceLabelEl!: HTMLDivElement;
  private rotateHintsEl!: HTMLDivElement;
  private rotateHintTopEl!: HTMLDivElement;
  private rotateHintLeftEl!: HTMLDivElement;
  private rotateHintRightEl!: HTMLDivElement;
  private rotateHintCenterEl!: HTMLDivElement;
  private instanceBoundsHelper: THREE.Box3Helper | null = null;
  private instanceBoundsTarget: THREE.Object3D | null = null;
  private rotateHintsTarget: THREE.Object3D | null = null;
  private instanceBoundsBox = new THREE.Box3();
  private rotateHintsBox = new THREE.Box3();
  private _tmpCenter = new THREE.Vector3();
  private _tmpSize = new THREE.Vector3();
  private _tmpNdc = new THREE.Vector3();
  private _tmpRotateTop = new THREE.Vector3();
  private _tmpRotateLeft = new THREE.Vector3();
  private _tmpRotateRight = new THREE.Vector3();
  private _tmpDragWorld = new THREE.Vector3();
  private _tmpFloorClampBox = new THREE.Box3();
  private _tmpFloorClampDelta = new THREE.Vector3();
  private _tmpFloorClampQuat = new THREE.Quaternion();

  // Grid styling constants (kept here so constructor + theme toggles stay consistent).
  // Lift the grid enough to avoid depth precision issues (z-fighting) with the shadow floor.
  // (Values << 1 LDU are visually "on the floor" but robust across camera distances.)
  private readonly GRID_Y = 0.35;

  // Workspace bounds: we intentionally treat the visible grid as the "build area".
  // New parts should not spawn outside it (prevents "way off in the distance" placement
  // when the camera ray hits the ground plane very far away).
  private readonly GRID_SIZE = 2000; // must match GridHelper size
  private readonly WORKSPACE_HALF = this.GRID_SIZE / 2;
  
  private activeConstraint:
    | {
        otherObj: THREE.Object3D;
        aId: string;
        bId: string;
        startQuat: THREE.Quaternion;
      }
    | null = null;

  constructor(host: HTMLElement, cb: SceneManagerCallbacks) {
    this.host = host;
    this.callbacks = cb;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x070a10);
    this.scene.fog = new THREE.Fog(0x070a10, 240, 1400);

    const w = host.clientWidth || 1;
    const h = host.clientHeight || 1;
    this.camera = new THREE.PerspectiveCamera(55, w / h, 1, 5000);
    this.camera.position.set(420, 340, 520);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    (this.renderer as any).physicallyCorrectLights = true;
    host.appendChild(this.renderer.domElement);

    // Track whether the pointer is currently over the 3D canvas.
    // This lets the UI place parts at a sensible default when you click in side panels.
    const onEnter = () => { this.pointerInCanvas = true; };
    const onLeave = () => { this.pointerInCanvas = false; };
    this.renderer.domElement.addEventListener('pointerenter', onEnter);
    this.renderer.domElement.addEventListener('pointerleave', onLeave);
    this.disposeFns.push(() => this.renderer.domElement.removeEventListener('pointerenter', onEnter));
    this.disposeFns.push(() => this.renderer.domElement.removeEventListener('pointerleave', onLeave));

    // 2D overlay for selection labels
    this.overlayEl = document.createElement('div');
    this.overlayEl.className = 'canvasOverlay';
    host.appendChild(this.overlayEl);

    this.instanceLabelEl = document.createElement('div');
    this.instanceLabelEl.className = 'instanceTag';
    this.instanceLabelEl.style.display = 'none';
    this.overlayEl.appendChild(this.instanceLabelEl);
    this.initRotateHintsOverlay();

    this.disposeFns.push(() => {
      this.overlayEl.remove();
    });

    // Nice environment for PBR (used primarily in Render mode)
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environment = env;
    this.disposeFns.push(() => {
      env.dispose();
      pmrem.dispose();
    });

    // Lighting
    this.hemi = new THREE.HemisphereLight(0xa2c8ff, 0x1b2330, 0.65);
    this.scene.add(this.hemi);
    this.dir = new THREE.DirectionalLight(0xffffff, 1.35);
    this.dir.position.set(520, 780, 350);
    this.dir.castShadow = true;
    this.dir.shadow.mapSize.set(2048, 2048);
    this.dir.shadow.camera.near = 1;
    this.dir.shadow.camera.far = 2200;
    this.dir.shadow.camera.left = -900;
    this.dir.shadow.camera.right = 900;
    this.dir.shadow.camera.top = 900;
    this.dir.shadow.camera.bottom = -900;
    this.scene.add(this.dir);

    // Floor + grid
    this.floor = new THREE.Mesh(
      new THREE.PlaneGeometry(5000, 5000),
      new THREE.ShadowMaterial({ opacity: 0.22 })
    );
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = true;
    this.floor.position.y = 0;
    // Push the floor a tiny bit "back" in the depth buffer so the grid lines can sit above it cleanly.
    // ShadowMaterial still receives shadows with polygonOffset enabled.
    const fm = this.floor.material as THREE.ShadowMaterial & any;
    fm.polygonOffset = true;
    fm.polygonOffsetFactor = 1;
    fm.polygonOffsetUnits = 1;
    this.scene.add(this.floor);

    // Grid: use the same path as theme switching so dark mode is visible from first render.
    // In dark mode, use a near-white/blue-gray so it reads against the dark fog.
    this.rebuildGrid(0x9fb2d2, 0x4d6a88, 0.48);

    const axes = new THREE.AxesHelper(120);
    axes.position.set(-220, 0.1, -220);
    this.scene.add(axes);

    this.scene.add(this.partsRoot);

    // Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 80, 0);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 80;
    this.controls.maxDistance = 2200;

    this.transform = new TransformControls(this.camera, this.renderer.domElement);
    this.transform.setSize(0.7);
    this.transform.setSpace('world');
    this.transform.setMode('rotate');
    this.transform.addEventListener('dragging-changed', (e: any) => {
      this.controls.enabled = !e?.value;
    });

    let dragging = false;
    this.transform.addEventListener('mouseDown', () => {
      dragging = true;
      this.dragTranslateUsedSmartSolve = false;
      const obj = this.transform.object;
      this.dragOthers = obj ? this.getOtherLeafParts(obj) : null;
      this.captureConstraint();

      if (obj) {
        obj.updateMatrix();
        obj.updateMatrixWorld(true);
        obj.getWorldPosition(this._tmpDragWorld);
        this.dragSolveState = {
          previousCandidateKey: null,
          previousScore: 0,
          previousY: this._tmpDragWorld.y,
        };
      } else {
        this.dragSolveState = null;
      }
      this.clearDragDebug();
    });
    this.transform.addEventListener('mouseUp', () => {
      if (!dragging) return;
      dragging = false;
      const obj = this.transform.object;
      const snap = this.callbacks.getSnap();

      const tcMode =
        ((this.transform as any).mode as string | undefined) ??
        ((this.transform as any).getMode?.() as string | undefined) ??
        'translate';

      if (obj && tcMode === 'translate' && this.dragTranslateUsedSmartSolve) {
        const others = this.dragOthers ?? this.getOtherLeafParts(obj);
        this.applySmartDragTranslate(obj, others, snap, true);
      }

      this.dragOthers = null;
      this.activeConstraint = null;
      this.dragSolveState = null;
      this.dragTranslateUsedSmartSolve = false;
      this.clearDragDebug();

      if (!obj) return;
      const id = (obj.userData?.partId as string | undefined) ?? null;
      if (!id) return;
      this.clampObjectAboveFloor(obj);
      obj.updateMatrix();
      this.callbacks.onMatrixChange(id, obj.matrix.elements.slice(), true);
    });
    this.transform.addEventListener('objectChange', () => {
      const obj = this.transform.object;
      if (!obj) return;
      const id = (obj.userData?.partId as string | undefined) ?? null;
      if (!id) return;
      const snap = this.callbacks.getSnap();
      const mode = this.callbacks.getMode();

      const tcMode =
        ((this.transform as any).mode as string | undefined) ??
        ((this.transform as any).getMode?.() as string | undefined) ??
        'translate';

      const others = this.dragOthers ?? this.getOtherLeafParts(obj);
      const hingeActive = tcMode === 'rotate' && snap.hingeSolver && !!this.activeConstraint;

      if (tcMode === 'translate') {
        const axis = String((this.transform as any).axis ?? '');
        const verticalHandle = axis === 'Y' || axis === 'XY' || axis === 'YZ';
        if (verticalHandle) {
          // User is explicitly dragging on a Y-capable handle; don't override Y with smart solver.
          this.dragTranslateUsedSmartSolve = false;
          applySnapToObject(obj, snap, mode);
          obj.updateMatrix();
          obj.updateMatrixWorld(true);
          obj.getWorldPosition(this._tmpDragWorld);
          if (this.dragSolveState) {
            this.dragSolveState.previousY = this._tmpDragWorld.y;
            this.dragSolveState.previousCandidateKey = null;
            this.dragSolveState.previousScore = 0;
          }
          this.clearDragDebug();
        } else {
          this.dragTranslateUsedSmartSolve = true;
          this.applySmartDragTranslate(obj, others, snap, false);
        }
      } else {
        // In hinge mode we must NOT grid/height/axis-euler snap the object, because we will
        // project rotation onto the hinge axis and keep the pivot locked.
        if (!hingeActive) {
          applySnapToObject(obj, snap, mode);
        }

        // Hinge solver = lock pivot AND limit rotation to the hinge axis (twist-only).
        if (hingeActive && this.activeConstraint) {
          const ok = applyHingeAxisConstraint(
            obj,
            this.activeConstraint.otherObj,
            this.activeConstraint.aId,
            this.activeConstraint.bId,
            this.activeConstraint.startQuat,
            snap
          );
          if (!ok) applyConnectorSnap(obj, others, snap);
        } else {
          applyConnectorSnap(obj, others, snap);
        }
      }

      this.clampObjectAboveFloor(obj);

      // Ensure matrix is current after snapping (matrixAutoUpdate is false for parts)
      obj.updateMatrix();
      this.callbacks.onMatrixChange(id, obj.matrix.elements.slice(), false);
    });

    // ✅ Newer three.js: TransformControls is not an Object3D; add its helper to the scene.
    this.transformHelper = this.transform.getHelper();
    this.styleTransformGizmoMinimal();
    this.scene.add(this.transformHelper);

    // Post processing
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.ssao = new SSAOPass(this.scene, this.camera, w, h);
    this.ssao.kernelRadius = 16;
    this.ssao.minDistance = 0.005;
    this.ssao.maxDistance = 0.16;
    this.composer.addPass(this.ssao);
    this.outline = new OutlinePass(new THREE.Vector2(w, h), this.scene, this.camera);
    this.outline.edgeStrength = 4.0;
    this.outline.edgeGlow = 0.2;
    this.outline.edgeThickness = 1.2;
    this.outline.visibleEdgeColor.set('#7c5cff');
    this.outline.hiddenEdgeColor.set('#00d0ff');
    this.composer.addPass(this.outline);

    this.bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.55, 0.7, 0.85);
    this.bloom.enabled = false;
    this.composer.addPass(this.bloom);

    this.smaa = new SMAAPass();
    this.smaa.setSize(w, h);
    this.smaa.enabled = true;
    this.composer.addPass(this.smaa);

    // Events
    const onResize = () => this.resize();
    window.addEventListener('resize', onResize);
    this.disposeFns.push(() => window.removeEventListener('resize', onResize));

    const onPointerDown = (ev: PointerEvent) => {
      const consumed = this.pick(ev);
      if (!consumed) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
    };
    this.renderer.domElement.addEventListener('pointerdown', onPointerDown, true);
    this.disposeFns.push(() => this.renderer.domElement.removeEventListener('pointerdown', onPointerDown, true));

    const onPointerMove = (ev: PointerEvent) => this.trackPlacementPoint(ev);
    this.renderer.domElement.addEventListener('pointermove', onPointerMove);
    this.disposeFns.push(() => this.renderer.domElement.removeEventListener('pointermove', onPointerMove));

    const onWindowPointerMove = (ev: PointerEvent) => {
      this.updateFreeDrag(ev);
    };
    window.addEventListener('pointermove', onWindowPointerMove);
    this.disposeFns.push(() => window.removeEventListener('pointermove', onWindowPointerMove));

    const onPointerUp = () => this.finishFreeDrag(false);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    this.disposeFns.push(() => window.removeEventListener('pointerup', onPointerUp));
    this.disposeFns.push(() => window.removeEventListener('pointercancel', onPointerUp));
    
    const onKeyDown = (ev: KeyboardEvent) => this.onKey(ev);
    window.addEventListener('keydown', onKeyDown);
    this.disposeFns.push(() => window.removeEventListener('keydown', onKeyDown));

    this.animate();
  }

  // Exposed for UI placement (PartBrowser).
  getPlacementPoint(): THREE.Vector3 | null {
   // Sidebar workflow: hover in the canvas, then click in the sidebar.
   // The mouse leaves the canvas, so `pointerInCanvas` is false.
   // Use a time-based TTL instead of pointerInCanvas gating.
   const now = performance.now();
   if (this.lastPlacePoint && (now - this.lastPlaceTime) < this.PLACE_POINT_TTL_MS) {
     const p = this.lastPlacePoint.clone();
     const sane = this.sanitizePlacementPoint(p);
     if (sane) return sane.clone();
   }

   // Fallback: use orbit target XZ, but compute Y by raycasting down onto the model
   // so "add another part" stacks instead of overlaps.
   const x = this.controls.target.x;
   const z = this.controls.target.z;
   const y = this.pickTopYAt(x, z);

   const fallback = new THREE.Vector3(x, y, z);
   const sane = this.sanitizePlacementPoint(fallback);
   return sane ? sane.clone() : new THREE.Vector3(0, 0, 0);
  }

  async getPlacementOffsetY(partFile: string): Promise<number> {
    return this.lib.getPlacementOffsetY(partFile);
  }

  setPlacementDraft(draft: PendingPlacement | null) {
    this.placementDraft = draft ? { ...draft } : null;
    this.placementDraftSerial += 1;
    const serial = this.placementDraftSerial;

    this.clearPlacementPreview();
    this.placementOffsetY = 0;
    this.placementOffsetPromise = null;

    if (!this.placementDraft || this.uiMode !== 'edit') return;

    const d = this.placementDraft;
    this.placementOffsetPromise = this.lib
      .getPlacementOffsetY(d.partFile)
      .then((offset) => {
        if (serial !== this.placementDraftSerial) return this.placementOffsetY;
        this.placementOffsetY = Number.isFinite(offset) ? offset : 0;
        this.refreshPlacementPreview();
        return this.placementOffsetY;
      })
      .catch(() => 0);

    void this.lib
      .createPart(d.partFile, d.ldrawColor, d.colorHex)
      .then((obj) => {
        if (serial !== this.placementDraftSerial) return;
        this.stylePlacementPreview(obj);
        this.placementPreview = obj;
        this.scene.add(obj);
        this.refreshPlacementPreview();
      })
      .catch(() => {});
  }

  private clearPlacementPreview() {
    if (!this.placementPreview) return;
    this.scene.remove(this.placementPreview);
    this.disposePlacementPreviewMaterials(this.placementPreview);
    this.placementPreview = null;
  }

  private stylePlacementPreview(obj: THREE.Object3D) {
    obj.visible = false;
    obj.traverse((c: THREE.Object3D) => {
      const mesh = c as THREE.Mesh;
      if (!mesh.isMesh) return;
      const current = mesh.material;
      if (Array.isArray(current)) {
        mesh.material = current.map((m) => this.makePlacementGhostMaterial(m));
      } else if (current) {
        mesh.material = this.makePlacementGhostMaterial(current);
      }
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = 999;
      mesh.frustumCulled = false;
    });
  }

  private makePlacementGhostMaterial(src: THREE.Material): THREE.Material {
    const m = src.clone() as any;
    m.transparent = true;
    m.opacity = 0.55;
    m.depthWrite = false;
    if ('emissive' in m && m.emissive?.isColor) {
      m.emissive = m.emissive.clone();
      m.emissive.addScalar(0.08);
    }
    if ('polygonOffset' in m) {
      m.polygonOffset = true;
      m.polygonOffsetFactor = -1;
      m.polygonOffsetUnits = -1;
    }
    return m as THREE.Material;
  }

  private disposePlacementPreviewMaterials(obj: THREE.Object3D) {
    obj.traverse((c: THREE.Object3D) => {
      const mesh = c as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mat = mesh.material as any;
      if (Array.isArray(mat)) {
        for (const m of mat) m?.dispose?.();
      } else {
        mat?.dispose?.();
      }
    });
  }

  private refreshPlacementPreview() {
    if (!this.placementPreview || !this.placementDraft || this.uiMode !== 'edit') return;
    const base = this.getPlacementPoint();
    if (!base) {
      this.placementPreview.visible = false;
      return;
    }
    const placed = this.getFinalPlacementPoint(base, this.placementOffsetY);
    this.placementPreview.position.copy(placed);
    this.placementPreview.rotation.set(0, 0, 0);
    this.placementPreview.scale.set(1, 1, 1);
    this.placementPreview.updateMatrix();
    this.placementPreview.updateMatrixWorld(true);
    this.placementPreview.visible = true;
  }

  private async resolvePlacementOffset(serial: number): Promise<number> {
    if (!this.placementOffsetPromise) return this.placementOffsetY;
    try {
      const next = await this.placementOffsetPromise;
      if (serial !== this.placementDraftSerial) return this.placementOffsetY;
      this.placementOffsetY = Number.isFinite(next) ? next : this.placementOffsetY;
    } catch {}
    return this.placementOffsetY;
  }

  private snapValueFloor(v: number, step: number): number {
    if (step <= 0) return v;
    return Math.floor(v / step) * step;
  }

  private getFinalPlacementPoint(base: THREE.Vector3, placementOffsetY: number): THREE.Vector3 {
    const p = base.clone();
    p.y += placementOffsetY;
    const snap = this.callbacks.getSnap();
    if (snap.enabled && snap.gridSnap) {
      p.x = snapValue(p.x, snap.grid);
      p.z = snapValue(p.z, snap.grid);
    }
    if (snap.enabled && snap.heightSnap) {
      p.y = this.snapValueFloor(p.y, snap.height);
    }
    return this.sanitizePlacementPoint(p) ?? p;
  }

  private styleTransformGizmoMinimal() {
    if (!this.transformHelper) return;

    const mode =
      ((this.transform as any).mode as string | undefined) ??
      ((this.transform as any).getMode?.() as string | undefined) ??
      'rotate';
    const rotateMode = mode === 'rotate';
    const hiddenNames = new Set(['E', 'XYZE', 'START', 'END', 'DELTA']);
    this.transform.setSize(rotateMode ? 0.42 : 0.7);

    this.transformHelper.traverse((obj: THREE.Object3D) => {
      const anyObj = obj as any;
      const name = String(anyObj?.name ?? '');
      const matRaw = anyObj?.material;
      const mats = Array.isArray(matRaw) ? matRaw : matRaw ? [matRaw] : [];
      const hide = rotateMode || hiddenNames.has(name);

      for (const mat of mats) {
        if (!mat) continue;
        if ('transparent' in mat) mat.transparent = true;
        if ('opacity' in mat) mat.opacity = hide ? 0 : 0.84;
        if ('depthTest' in mat) mat.depthTest = false;
        if ('depthWrite' in mat) mat.depthWrite = false;
        if ('toneMapped' in mat) mat.toneMapped = false;
        mat.needsUpdate = true;
      }
    });
  }

  private rebuildGrid(color1: number, color2: number, opacity = 0.7) {
    // Dispose the old grid (theme toggles can happen repeatedly).
    if (this.grid) {
      this.scene.remove(this.grid);
      try { (this.grid.geometry as any)?.dispose?.(); } catch {}
      const mat = this.grid.material as any;
      try {
        if (Array.isArray(mat)) mat.forEach((m) => m?.dispose?.());
        else mat?.dispose?.();
      } catch {}
    }

    this.grid = new THREE.GridHelper(this.GRID_SIZE, 100, color1, color2);
    // Lift grid slightly above the shadow floor to avoid z-fighting (especially in dark themes).
    this.grid.position.y = this.GRID_Y;
    (this.grid as any).renderOrder = 2;
    const mat = this.grid.material as any;
    if (Array.isArray(mat)) {
      for (const m of mat) {
        if (!m) continue;
        m.transparent = true;
        m.opacity = opacity;
        // Grid is a UI affordance; keep it stable even with ACES tonemapping + scene fog.
        m.toneMapped = false;
        m.fog = false;
        // Prevent the floor plane from "winning" the depth buffer and erasing the grid lines.
        m.depthWrite = false;
        m.depthTest = true;
      }
    } else if (mat) {
      mat.transparent = true;
      mat.opacity = opacity;
      mat.toneMapped = false;
      mat.fog = false;
      mat.depthWrite = false;
      mat.depthTest = true;
    }
    this.scene.add(this.grid);
  }

  private isFiniteVec3(v: THREE.Vector3): boolean {
    return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
  }

  private sanitizePlacementPoint(p: THREE.Vector3): THREE.Vector3 | null {
    if (!this.isFiniteVec3(p)) return null;

    // Never place "below the floor" via placement.
    if (p.y < 0) p.y = 0;

    // Clamp to the visible grid/workspace bounds so placements can't go outside the grid.
    p.x = THREE.MathUtils.clamp(p.x, -this.WORKSPACE_HALF, this.WORKSPACE_HALF);
    p.z = THREE.MathUtils.clamp(p.z, -this.WORKSPACE_HALF, this.WORKSPACE_HALF);
    return p;
  }

  private clampObjectAboveFloor(obj: THREE.Object3D): void {
    const box = this._tmpFloorClampBox;
    if (!getWorldCoreBoxForObject(obj, box)) {
      box.setFromObject(obj);
    }
    if (box.isEmpty()) return;
    const minY = box.min.y;
    if (!Number.isFinite(minY) || minY >= 0) return;

    const lift = -minY;
    if (obj.parent) {
      obj.parent.getWorldQuaternion(this._tmpFloorClampQuat);
      this._tmpFloorClampDelta.set(0, lift, 0).applyQuaternion(this._tmpFloorClampQuat.invert());
      obj.position.add(this._tmpFloorClampDelta);
    } else {
      obj.position.y += lift;
    }

    obj.updateMatrix();
    obj.updateMatrixWorld(true);
  }

  private pickTopYAt(x: number, z: number): number {
    // Use core bounds so stud caps do not raise stack height.
    return sampleCoreTopYAtXZ(x, z, this.leafParts);
  }

  private getPlacementCandidateFromClient(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -(((clientY - rect.top) / rect.height) * 2 - 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const objs = this.leafParts.filter((o) => o.visible);
    const hits = this.raycaster.intersectObjects(objs, true);
    if (hits.length) {
      const p = hits[0].point.clone();
      p.y = this.pickTopYAt(p.x, p.z);
      return p;
    }

    const fh = this.raycaster.intersectObject(this.floor, true);
    const candidate = fh[0]?.point?.clone?.() ?? null;
    if (!candidate) return null;
    candidate.y = this.pickTopYAt(candidate.x, candidate.z);
    return candidate;
  }

  private getDragCandidateFromClient(
    clientX: number,
    clientY: number,
    others: THREE.Object3D[]
  ): THREE.Vector3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -(((clientY - rect.top) / rect.height) * 2 - 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const visibleOthers = others.filter((o) => o.visible);
    const hits = this.raycaster.intersectObjects(visibleOthers, true);
    if (hits.length) {
      const p = hits[0].point.clone();
      p.y = sampleCoreTopYAtXZ(p.x, p.z, visibleOthers);
      return p;
    }

    const floorHit = this.raycaster.intersectObject(this.floor, true);
    const p = floorHit[0]?.point?.clone?.() ?? null;
    if (!p) return null;
    p.y = sampleCoreTopYAtXZ(p.x, p.z, visibleOthers);
    return p;
  }

  private getBottomOffsetY(obj: THREE.Object3D): number {
    const box = this._tmpFloorClampBox;
    if (!getWorldCoreBoxForObject(obj, box)) {
      box.setFromObject(obj);
    }
    if (box.isEmpty() || !Number.isFinite(box.min.y)) return 0;
    obj.updateMatrixWorld(true);
    obj.getWorldPosition(this._tmpDragWorld);
    return this._tmpDragWorld.y - box.min.y;
  }

  private startFreeDrag(ev: PointerEvent, id: string, obj: THREE.Object3D) {
    const others = this.getOtherLeafParts(obj);
    this.rotateHintsTarget = obj;
    this.freeDrag = {
      pointerId: ev.pointerId,
      id,
      obj,
      others,
      startX: ev.clientX,
      startY: ev.clientY,
      moved: false,
      bottomOffsetY: this.getBottomOffsetY(obj),
    };

    this.dragOthers = others;
    this.activeConstraint = null;
    this.dragTranslateUsedSmartSolve = true;
    obj.updateMatrix();
    obj.updateMatrixWorld(true);
    obj.getWorldPosition(this._tmpDragWorld);
    this.dragSolveState = {
      previousCandidateKey: null,
      previousScore: 0,
      previousY: this._tmpDragWorld.y,
    };
    this.controls.enabled = false;
    this.clearDragDebug();
    this.updateRotateHintsViz();
  }

  private updateFreeDrag(ev: PointerEvent): boolean {
    const st = this.freeDrag;
    if (!st) return false;
    if (ev.pointerId !== st.pointerId) return true;
    if (this.uiMode !== 'edit') {
      this.finishFreeDrag(true);
      return true;
    }
    if ((this.transform as any).dragging) {
      this.finishFreeDrag(true);
      return true;
    }

    const dist = Math.hypot(ev.clientX - st.startX, ev.clientY - st.startY);
    if (!st.moved && dist < this.FREE_DRAG_THRESHOLD_PX) return true;
    st.moved = true;
    this.controls.enabled = false;

    const base = this.getDragCandidateFromClient(ev.clientX, ev.clientY, st.others);
    if (!base) return true;
    const sane = this.sanitizePlacementPoint(base);
    if (!sane) return true;

    st.obj.position.set(sane.x, sane.y + st.bottomOffsetY, sane.z);
    const snap = this.callbacks.getSnap();
    this.dragTranslateUsedSmartSolve = true;
    this.applySmartDragTranslate(st.obj, st.others, snap, false);
    this.clampObjectAboveFloor(st.obj);
    st.obj.updateMatrix();
    this.callbacks.onMatrixChange(st.id, st.obj.matrix.elements.slice(), false);
    return true;
  }

  private finishFreeDrag(cancel: boolean) {
    const st = this.freeDrag;
    if (!st) return;

    if (!cancel && st.moved) {
      const snap = this.callbacks.getSnap();
      this.applySmartDragTranslate(st.obj, st.others, snap, true);
      this.clampObjectAboveFloor(st.obj);
      st.obj.updateMatrix();
      this.callbacks.onMatrixChange(st.id, st.obj.matrix.elements.slice(), true);
    }

    this.freeDrag = null;
    this.dragOthers = null;
    this.dragSolveState = null;
    this.activeConstraint = null;
    this.dragTranslateUsedSmartSolve = false;
    this.controls.enabled = !(this.transform as any).dragging;
    this.clearDragDebug();
    this.updateRotateHintsViz();
  }

  private rememberPlacementCandidate(candidate: THREE.Vector3 | null): THREE.Vector3 | null {
    if (!candidate) return null;
    const sane = this.sanitizePlacementPoint(candidate);
    if (!sane) {
      // If sanitize fails (NaN/Infinity), clear cached placement so UI falls back safely.
      this.lastPlacePoint = null;
      return null;
    }
    this.lastPlacePoint = sane;
    this.lastPlaceTime = performance.now();
    return sane.clone();
  }

  private trackPlacementPoint(ev: PointerEvent) {
    // trackPlacementPoint only runs when the pointer is moving over the canvas.
    this.pointerInCanvas = true;
    const sane = this.rememberPlacementCandidate(
      this.getPlacementCandidateFromClient(ev.clientX, ev.clientY)
    );
    if (!sane) {
      if (this.placementPreview) this.placementPreview.visible = false;
      return;
    }
    this.refreshPlacementPreview();
  }

  private async commitPendingPlacement(ev: PointerEvent): Promise<boolean> {
    if (!this.placementDraft || this.uiMode !== 'edit') return false;

    const base =
      this.rememberPlacementCandidate(this.getPlacementCandidateFromClient(ev.clientX, ev.clientY)) ??
      this.getPlacementPoint();
    if (!base) return false;

    const serial = this.placementDraftSerial;
    const offset = await this.resolvePlacementOffset(serial);
    if (serial !== this.placementDraftSerial || !this.placementDraft) return true;

    const placed = this.getFinalPlacementPoint(base, offset);
    this.setPlacementDraft(null);
    this.callbacks.onPlacementCommit({ x: placed.x, y: placed.y, z: placed.z });
    return true;
  }

  private captureConstraint() {
    const obj = this.transform.object;
    if (!obj) return;
    const snap = this.callbacks.getSnap();
    if (!snap.enabled || !snap.connectorSnap) return;

    const tcMode =
      ((this.transform as any).mode as string | undefined) ??
      ((this.transform as any).getMode?.() as string | undefined) ??
      'translate';

    // Only bother locking a constraint for rotation (hinge solver use-case).
    if (tcMode !== 'rotate') {
      this.activeConstraint = null;
      return;
    }

    const others = this.dragOthers ?? this.getOtherLeafParts(obj);
    const match = findBestConnectorMatch(obj, others, snap);
    if (!match) {
      this.activeConstraint = null;
      return;
    }

    const aId = match.a.id ?? '';
    const bId = match.b.id ?? '';
    if (!aId || !bId) {
      this.activeConstraint = null;
      return;
    }

    this.activeConstraint = {
      otherObj: match.other,
      aId,
      bId,
      // Store WORLD quat so hinge solver stays correct even if we later edit inside instances.
      startQuat: obj.getWorldQuaternion(new THREE.Quaternion()),
    };
  }

  setRenderMode(mode: RenderMode) {
    this.renderMode = mode;
    if (mode === 'render') {
      this.ssao.enabled = true;
      this.outline.enabled = false;
      this.bloom.enabled = true;
      this.renderer.toneMappingExposure = 1.15;
    } else {
      this.ssao.enabled = true;
      this.outline.enabled = true;
      this.bloom.enabled = false;
      this.renderer.toneMappingExposure = 1.05;
    }
  }

  setTheme(theme: 'dark' | 'light') {
    const next: 'dark' | 'light' = theme === 'light' ? 'light' : 'dark';
    if (this.theme === next) return;
    this.theme = next;

    if (next === 'light') {
      // Light, paper-like workspace.
      this.scene.background = new THREE.Color(0xf4f7fb);
      this.scene.fog = new THREE.Fog(0xf4f7fb, 260, 1700);

      this.hemi.color.setHex(0xffffff);
      this.hemi.groundColor.setHex(0xdfe7f2);
      this.hemi.intensity = 0.85;

      this.dir.intensity = 1.2;

      const sm = this.floor.material as THREE.ShadowMaterial;
      sm.opacity = 0.14;

      this.rebuildGrid(0xb7c0cc, 0xd5dbe4, 0.55);
    } else {
      // Default dark workspace.
      this.scene.background = new THREE.Color(0x070a10);
      this.scene.fog = new THREE.Fog(0x070a10, 240, 1400);

      this.hemi.color.setHex(0xa2c8ff);
      this.hemi.groundColor.setHex(0x1b2330);
      this.hemi.intensity = 0.65;

      this.dir.intensity = 1.35;

      const sm = this.floor.material as THREE.ShadowMaterial;
      sm.opacity = 0.22;

      // Dark theme: readable grid (near-white/blue-gray), but still subtle.
      this.rebuildGrid(0x9fb2d2, 0x4d6a88, 0.48);
    }
  }
  
  setUiMode(mode: 'edit' | 'instructions') {
    this.uiMode = mode;
    if (mode === 'instructions') {
      this.finishFreeDrag(true);
      this.setPlacementDraft(null);
      this.transform.detach();
      this.outline.selectedObjects = [];
      this.clearDragDebug();
    } else {
      this.transform.setMode('rotate');
      this.styleTransformGizmoMinimal();
    }
    this.applyVisibility();
    this.updateRotateHintsViz();
    this.callbacks.onCollision('ok');
  }

  setSubmodelFilter(id: string | null) {
    this.submodelFilter = id;
    // Rebuild render tree for new context.
    this.syncParts(this.lastParts, this.lastInstances);
  }

  setViewStep(step: number) {
    this.viewStep = step;
    this.applyVisibility();
    if (this.uiMode === 'instructions') this.focusOnStep(this.viewStep);
  }

  private applyVisibility() {
    for (const obj of this.renderMap.values()) {
      const s = (obj.userData?.effectiveStep as number | undefined) ?? (obj.userData?.step as number | undefined) ?? 1;
      obj.visible = s <= this.viewStep;
    }
  }

  private focusOnStep(step: number) {
    // Frame the current step's parts; if empty, frame all visible.
    const box = new THREE.Box3();
    let found = false;
    for (const obj of this.leafParts) {
      if (!obj.visible) continue;
      const s = (obj.userData?.effectiveStep as number | undefined) ?? (obj.userData?.step as number | undefined) ?? 1;
      if (s !== step) continue;
      box.union(new THREE.Box3().setFromObject(obj));
      found = true;
    }
    if (!found) {
      for (const obj of this.leafParts) {
        if (!obj.visible) continue;
        box.union(new THREE.Box3().setFromObject(obj));
        found = true;
      }
    }
    if (!found) return;
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);
    const radius = Math.max(40, size.length() * 0.65);
    this.controls.target.copy(center);
    // Keep current polar-ish angle, just adjust distance.
    const dir = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();
    this.camera.position.copy(center).add(dir.multiplyScalar(radius * 2.0));
    this.camera.updateProjectionMatrix();
  }  

  async syncParts(parts: PlacedPart[], instances: SubmodelInstance[] = []) {
    // If the model was cleared (New model / delete all), reset placement cache
    // so a new project can't inherit a stale far-away placement point.
    const wasEmpty = (this.lastParts.length === 0) && (this.lastInstances.length === 0);
    const isEmpty = ((parts?.length ?? 0) === 0) && ((instances?.length ?? 0) === 0);
    if (isEmpty && !wasEmpty) {
      this.lastPlacePoint = null;
      this.lastPlaceTime = 0;
    }
    
    // Cache for re-render on context change.
    this.lastParts = parts ?? [];
    this.lastInstances = instances ?? [];

    const contextId = this.submodelFilter ?? MAIN_SUBMODEL_ID;

    // Build desired render graph keys for this context.
    const desired = new Set<string>();
    const selectable = new Map<string, THREE.Object3D>();
    const leafParts: THREE.Object3D[] = [];

    const partsBySub = new Map<string, PlacedPart[]>();
    for (const p of parts) {
      const sid = (p.submodelId ?? MAIN_SUBMODEL_ID);
      if (!partsBySub.has(sid)) partsBySub.set(sid, []);
      partsBySub.get(sid)!.push(p);
    }
    const instByParent = new Map<string, SubmodelInstance[]>();
    for (const ins of instances) {
      const pid = (ins.parentSubmodelId ?? MAIN_SUBMODEL_ID);
      if (!instByParent.has(pid)) instByParent.set(pid, []);
      instByParent.get(pid)!.push(ins);
    }

    const partKey = (path: string, id: string) => `part:${path ? path + '/' : ''}${id}`;
    const instKey = (path: string, id: string) => `inst:${path ? path + '/' : ''}${id}`;

    const syncSubmodel = async (
      submodelId: string,
      parent: THREE.Object3D,
      path: string,
      stepOffset: number,
      selectableDepth: boolean,
      stack: Set<string>,
      lockStep: number | null
    ) => {
      // ✅ cycle guard: submodelId only (path changes every level, so path-based guards don't work)
      if (stack.has(submodelId)) return;
      stack.add(submodelId);

      try {
        const listP = (partsBySub.get(submodelId) ?? []).slice().sort((a, b) => a.id.localeCompare(b.id));
        const listI = (instByParent.get(submodelId) ?? []).slice().sort((a, b) => a.id.localeCompare(b.id));

      // Parts
        for (const p of listP) {
          const k = partKey(path, p.id);
          desired.add(k);

        let obj = this.renderMap.get(k);
          if (!obj) {
            if (!this.createPromises.has(k)) {
              this.createPromises.set(
                k,
                this.lib.createPart(p.partFile, p.ldrawColor, p.colorHex).then((o) => {
                  o.matrixAutoUpdate = false;
                  (o.userData as any).kind = 'part';
                  return o;
                })
              );
            }
            obj = await this.createPromises.get(k)!;
            this.renderMap.set(k, obj);
            parent.add(obj);
          } else if (obj.parent !== parent) {
            parent.add(obj);
          }

        // Selection is only allowed for direct children in the current context.
          if (selectableDepth) {
            obj.userData.partId = p.id;
            selectable.set(p.id, obj);
          } else {
            delete (obj.userData as any).partId;
          }

        obj.userData.partFile = p.partFile;
          obj.userData.step = p.step;
          // If we are rendering inside an instance placement, lock all descendants to the
          // instance's own step so the whole submodel appears atomically in the parent.
          obj.userData.effectiveStep = lockStep ?? (stepOffset + (p.step ?? 1));
          obj.userData.submodelId = p.submodelId ?? MAIN_SUBMODEL_ID;
          obj.visible = (obj.userData.effectiveStep as number) <= this.viewStep;

        obj.matrix.fromArray(p.matrix);
          obj.matrix.decompose(obj.position, obj.quaternion, obj.scale);
          obj.updateMatrix();

        leafParts.push(obj);
        }

        // Instances
        for (const ins of listI) {
          const k = instKey(path, ins.id);
          desired.add(k);

          let grp = this.renderMap.get(k) as THREE.Object3D | undefined;
          if (!grp) {
            grp = new THREE.Group();
            grp.matrixAutoUpdate = false;
            (grp.userData as any).kind = 'instance';
            this.renderMap.set(k, grp);
            parent.add(grp);
          } else if (grp.parent !== parent) {
            parent.add(grp);
          }

          if (selectableDepth) {
            grp.userData.partId = ins.id;
            selectable.set(ins.id, grp);
          } else {
            delete (grp.userData as any).partId;
          }

          grp.userData.step = ins.step;
          const instEff = lockStep ?? (stepOffset + (ins.step ?? 1));
          grp.userData.effectiveStep = instEff;
          grp.userData.submodelId = ins.parentSubmodelId ?? MAIN_SUBMODEL_ID;
          grp.userData.instanceSubmodelId = ins.submodelId;
          grp.visible = (grp.userData.effectiveStep as number) <= this.viewStep;

          grp.matrix.fromArray(ins.matrix);
          grp.matrix.decompose(grp.position, grp.quaternion, grp.scale);
          grp.updateMatrix();
          const nextPath = path ? `${path}/${ins.id}` : ins.id;
          const nextOffset = stepOffset;
          const nextLock = lockStep ?? instEff;
          await syncSubmodel(ins.submodelId, grp, nextPath, nextOffset, false, stack, nextLock);
        }
      } finally {
        stack.delete(submodelId);
      }
    };

    // Render root context.
    const stack = new Set<string>();
    await syncSubmodel(contextId, this.partsRoot, '', 0, true, stack, null);

    // Remove orphaned render nodes.
    for (const [k, obj] of [...this.renderMap.entries()]) {
      if (!desired.has(k)) {
        obj.parent?.remove(obj);
        this.renderMap.delete(k);
        this.createPromises.delete(k);
      }
    }

    // Replace selectable map + leaf parts list.
    this.partMap = selectable;
    this.leafParts = leafParts;

    // Keep selection attached if possible.
    const selectedObj = this.transform.object;
    if (selectedObj) {
      const selId = (selectedObj.userData?.partId as string | undefined) ?? null;
      if (selId && this.partMap.get(selId) !== selectedObj) {
        this.setSelected(selId);
      }
    }
  }

  setSelected(id: string | null) {
    const obj = id ? this.partMap.get(id) ?? null : null;
    if (obj) {
      this.rotateHintsTarget = obj;
      const kind = (obj.userData?.kind as string | undefined) ?? 'part';
      if (this.uiMode === 'edit') this.transform.attach(obj);
      else this.transform.detach();
      this.styleTransformGizmoMinimal();
      if (kind === 'instance') {
        // Outline pass doesn't reliably disambiguate Group selections; use a bounds cue + tag.
        this.outline.selectedObjects = [];
        this.showInstanceSelection(obj);
      } else {
        this.hideInstanceSelection();
        this.outline.selectedObjects = [obj];
      }
    } else {
      this.rotateHintsTarget = null;
      this.finishFreeDrag(true);
      this.transform.detach();
      this.outline.selectedObjects = [];
      this.hideInstanceSelection();
      this.clearDragDebug();
    }
    this.updateRotateHintsViz();
  }

  private ensureInstanceBoundsHelper() {
    if (this.instanceBoundsHelper) return;
    this.instanceBoundsHelper = new THREE.Box3Helper(this.instanceBoundsBox, 0x7c5cff);
    const mat = this.instanceBoundsHelper.material as THREE.LineBasicMaterial;
    mat.transparent = true;
    mat.opacity = 0.55;
    mat.depthTest = false;
    (this.instanceBoundsHelper as any).renderOrder = 999;
    this.scene.add(this.instanceBoundsHelper);
  }

  private showInstanceSelection(obj: THREE.Object3D) {
    this.ensureInstanceBoundsHelper();
    this.instanceBoundsTarget = obj;
    this.instanceBoundsHelper!.visible = true;

    const childSmId = String(obj.userData?.instanceSubmodelId ?? '');
    const label = this.callbacks.getSubmodelLabel(childSmId) || childSmId || 'submodel';
    this.instanceLabelEl.innerHTML = `<b>Instance</b>: <span class="mono">${label}</span>`;
    this.instanceLabelEl.style.display = 'block';
    this.updateInstanceSelectionViz();
  }

  private hideInstanceSelection() {
    this.instanceBoundsTarget = null;
    if (this.instanceBoundsHelper) this.instanceBoundsHelper.visible = false;
    if (this.instanceLabelEl) this.instanceLabelEl.style.display = 'none';
  }

  private updateInstanceSelectionViz() {
    if (!this.instanceBoundsTarget || !this.instanceBoundsHelper) return;
    const obj = this.instanceBoundsTarget;
    if (!obj.visible) {
      this.instanceBoundsHelper.visible = false;
      this.instanceLabelEl.style.display = 'none';
      return;
    }

    this.instanceBoundsBox.setFromObject(obj);
    this.instanceBoundsHelper.box.copy(this.instanceBoundsBox);
    this.instanceBoundsHelper.updateMatrixWorld(true);
    this.instanceBoundsHelper.visible = true;

    // Place the label above the bounds
    this.instanceBoundsBox.getCenter(this._tmpCenter);
    this.instanceBoundsBox.getSize(this._tmpSize);
    this._tmpCenter.y += this._tmpSize.y * 0.5 + 12;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this._tmpNdc.copy(this._tmpCenter).project(this.camera);
    if (this._tmpNdc.z < -1 || this._tmpNdc.z > 1) {
      this.instanceLabelEl.style.display = 'none';
      return;
    }

    const x = (this._tmpNdc.x * 0.5 + 0.5) * rect.width;
    const y = (-this._tmpNdc.y * 0.5 + 0.5) * rect.height;

    this.instanceLabelEl.style.left = `${x}px`;
    this.instanceLabelEl.style.top = `${y}px`;
    this.instanceLabelEl.style.display = 'block';
  }

  private initRotateHintsOverlay() {
    this.rotateHintsEl = document.createElement('div');
    this.rotateHintsEl.className = 'rotateHints';
    this.rotateHintsEl.style.display = 'none';
    this.overlayEl.appendChild(this.rotateHintsEl);

    const addHint = (className: string) => {
      const el = document.createElement('div');
      el.className = className;
      this.rotateHintsEl.appendChild(el);
      return el;
    };

    this.rotateHintTopEl = addHint('rotateHint rotateHintTop');
    this.rotateHintLeftEl = addHint('rotateHint rotateHintLeft');
    this.rotateHintRightEl = addHint('rotateHint rotateHintRight');
    this.rotateHintCenterEl = addHint('rotateHintCenter');
  }

  private placeRotateHint(el: HTMLDivElement, world: THREE.Vector3, rect: DOMRect): boolean {
    this._tmpNdc.copy(world).project(this.camera);
    if (this._tmpNdc.z < -1 || this._tmpNdc.z > 1) {
      el.style.display = 'none';
      return false;
    }
    const x = (this._tmpNdc.x * 0.5 + 0.5) * rect.width;
    const y = (-this._tmpNdc.y * 0.5 + 0.5) * rect.height;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.display = 'block';
    return true;
  }

  private updateRotateHintsViz() {
    if (!this.rotateHintsEl) return;

    const obj = this.transform.object ?? this.freeDrag?.obj ?? this.rotateHintsTarget;
    const show = this.uiMode === 'edit' && !!obj;
    if (!show) {
      this.rotateHintsEl.style.display = 'none';
      return;
    }
    if (!obj.visible) {
      this.rotateHintsEl.style.display = 'none';
      return;
    }

    if (!getWorldCoreBoxForObject(obj, this.rotateHintsBox)) {
      this.rotateHintsBox.setFromObject(obj);
    }
    if (this.rotateHintsBox.isEmpty()) {
      this.rotateHintsEl.style.display = 'none';
      return;
    }

    this.rotateHintsBox.getCenter(this._tmpCenter);
    this.rotateHintsBox.getSize(this._tmpSize);

    const marginX = Math.max(10, this._tmpSize.x * 0.55);
    const marginY = Math.max(11, this._tmpSize.y * 0.6);
    const marginZ = Math.max(10, this._tmpSize.z * 0.55);

    this._tmpRotateTop.set(this._tmpCenter.x, this.rotateHintsBox.max.y + marginY, this._tmpCenter.z);
    this._tmpRotateLeft.set(this.rotateHintsBox.min.x - marginX, this.rotateHintsBox.min.y + marginY * 0.12, this._tmpCenter.z);
    this._tmpRotateRight.set(this._tmpCenter.x + marginZ, this._tmpCenter.y, this.rotateHintsBox.max.z + marginZ * 0.15);

    const rect = this.renderer.domElement.getBoundingClientRect();
    const topVisible = this.placeRotateHint(this.rotateHintTopEl, this._tmpRotateTop, rect);
    const leftVisible = this.placeRotateHint(this.rotateHintLeftEl, this._tmpRotateLeft, rect);
    const rightVisible = this.placeRotateHint(this.rotateHintRightEl, this._tmpRotateRight, rect);
    const centerVisible = this.placeRotateHint(this.rotateHintCenterEl, this._tmpCenter, rect);

    this.rotateHintsEl.style.display = topVisible || leftVisible || rightVisible || centerVisible ? 'block' : 'none';
  }

  private pick(ev: PointerEvent): boolean {
    // Ignore clicks while transforming
    if ((this.transform as any).dragging) return false;
    if (ev.button !== 0) return false;

    if (this.placementDraft && this.uiMode === 'edit') {
      void this.commitPendingPlacement(ev);
      return true;
    }

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const objs = [...this.partMap.values()].filter((o) => o.visible);
    const hits = this.raycaster.intersectObjects(objs, true);
    const hit = hits[0];
    const gizmoHitEntry = this.raycaster
      .intersectObject(this.transformHelper, true)
      .find((h) => {
        const mat = (h.object as any)?.material;
        if (Array.isArray(mat)) {
          return mat.some((m) => Number((m as any)?.opacity ?? 1) > 0.02);
        }
        return Number((mat as any)?.opacity ?? 1) > 0.02;
      });
    const gizmoHit = !!gizmoHitEntry && (!hit || gizmoHitEntry.distance <= hit.distance);

    if (!hit) {
      if (!gizmoHit) this.callbacks.onSelect(null);
      return false;
    }
    const root = this.findPartRoot(hit.object);
    const id = root?.userData?.partId as string | undefined;
    this.callbacks.onSelect(id ?? null);
    if (!id || !root || this.uiMode !== 'edit') return false;
    this.startFreeDrag(ev, id, root);
    return true;
  }

  private findPartRoot(o: THREE.Object3D): THREE.Object3D | null {
    let cur: THREE.Object3D | null = o;
    while (cur) {
      if (cur.userData?.partId) return cur;
      cur = cur.parent;
    }
    return null;
  }

  private onKey(ev: KeyboardEvent) {
    const t = ev.target as HTMLElement | null;
    const tag = (t?.tagName ?? '').toLowerCase();
    if ((t as any)?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select') return;
    
    if (this.uiMode === 'edit') {
      if (ev.key.toLowerCase() === 'w') {
        this.transform.setMode('translate');
        this.styleTransformGizmoMinimal();
        this.updateRotateHintsViz();
      }
      if (ev.key.toLowerCase() === 'e') {
        this.transform.setMode('rotate');
        this.styleTransformGizmoMinimal();
        this.updateRotateHintsViz();
      }
      if (ev.key.toLowerCase() === 'f') this.focusOnSelected();
    }
    if (ev.key === 'Escape') {
      if (this.placementDraft) {
        this.setPlacementDraft(null);
        return;
      }
      if (this.freeDrag) {
        this.finishFreeDrag(true);
        return;
      }
      this.callbacks.onSelect(null);
    }
  }

  private focusOnSelected() {
    const obj = this.transform.object;
    if (!obj) return;
    const box = new THREE.Box3().setFromObject(obj);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);
    const radius = Math.max(40, size.length() * 0.65);
    this.controls.target.copy(center);
    const dir = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();
    this.camera.position.copy(center).add(dir.multiplyScalar(radius * 2.0));
    this.camera.updateProjectionMatrix();
  }

  private resize() {
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
    this.ssao.setSize(w, h);
    this.outline.setSize(w, h);
    (this.bloom as any).setSize?.(w, h);
    this.smaa.setSize(w, h);
  }

  private animate = () => {
    this.animationHandle = requestAnimationFrame(this.animate);
    this.controls.update();

    const now = performance.now();
    if (now - this.lastCollisionCheck > 200) {
      this.lastCollisionCheck = now;
      this.checkCollision();
    }

    // Render
    this.updateInstanceSelectionViz();
    this.updateRotateHintsViz();
    this.composer.render();
  };

  private checkCollision() {
    if (this.uiMode !== 'edit') {
      this.callbacks.onCollision('ok');
      return;
    }
    const selectedObj = this.transform.object;
    if (!selectedObj) {
      this.callbacks.onCollision('ok');
      return;
    }
    const others = this.dragOthers ?? this.getOtherLeafParts(selectedObj);
    const c = evaluateCollision(selectedObj, others);
    this.callbacks.onCollision(c);
  }

  private applySmartDragTranslate(
    obj: THREE.Object3D,
    others: THREE.Object3D[],
    snap: SnapSettings,
    finalize: boolean
  ) {
    if (this.uiMode !== 'edit') return;

    obj.updateMatrix();
    obj.updateMatrixWorld(true);
    obj.getWorldPosition(this._tmpDragWorld);

    const solved = SolveDragPlacement(obj, others, this._tmpDragWorld, snap, this.dragSolveState);
    this.dragSolveState = solved.state;
    ApplyBrickPose(obj, solved.finalPose);
    this.updateDragDebug(obj, solved);
    this.logChosenSnapDebug(solved, false);

    if (!finalize) return;

    // Drop-time revalidation: run the exact same solve once more at the final pose.
    obj.updateMatrix();
    obj.updateMatrixWorld(true);
    obj.getWorldPosition(this._tmpDragWorld);
    const validated = SolveDragPlacement(obj, others, this._tmpDragWorld, snap, this.dragSolveState);
    this.dragSolveState = validated.state;
    ApplyBrickPose(obj, validated.finalPose);
    this.updateDragDebug(obj, validated);
    this.logChosenSnapDebug(validated, true);
  }

  private ensureDragDebugHelpers() {
    if (!this.dragDebugPointsMat) {
      this.dragDebugPointsMat = new THREE.PointsMaterial({
        size: 4,
        sizeAttenuation: false,
        vertexColors: true,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.95,
      });
    }
    if (!this.dragDebugLineMat) {
      this.dragDebugLineMat = new THREE.LineBasicMaterial({
        color: 0xffd166,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.95,
      });
    }
    if (!this.dragGhostBoundsHelper) {
      this.dragGhostBoundsHelper = new THREE.Box3Helper(this.dragGhostBoundsBox, 0x24d2ff);
      const mat = this.dragGhostBoundsHelper.material as THREE.LineBasicMaterial;
      mat.depthTest = false;
      mat.depthWrite = false;
      mat.transparent = true;
      mat.opacity = 0.85;
      (this.dragGhostBoundsHelper as any).renderOrder = 1000;
      this.dragGhostBoundsHelper.visible = false;
      this.scene.add(this.dragGhostBoundsHelper);
    }
  }

  private updateDragDebug(obj: THREE.Object3D, solved: DragSolveResult) {
    if (!import.meta.env.DEV) return;
    this.ensureDragDebugHelpers();

    if (this.dragDebugPoints) {
      this.scene.remove(this.dragDebugPoints);
      this.dragDebugPoints.geometry.dispose();
      this.dragDebugPoints = null;
    }
    if (this.dragDebugPairLine) {
      this.scene.remove(this.dragDebugPairLine);
      this.dragDebugPairLine.geometry.dispose();
      this.dragDebugPairLine = null;
    }

    const dragPts = solved.debug.dragSnapPoints;
    const nearbyPts = solved.debug.nearbySnapPoints;
    const total = dragPts.length + nearbyPts.length;
    if (total > 0 && this.dragDebugPointsMat) {
      const pos = new Float32Array(total * 3);
      const col = new Float32Array(total * 3);

      let idx = 0;
      for (const p of dragPts) {
        pos[idx * 3 + 0] = p.x;
        pos[idx * 3 + 1] = p.y;
        pos[idx * 3 + 2] = p.z;
        col[idx * 3 + 0] = 0.1;
        col[idx * 3 + 1] = 0.85;
        col[idx * 3 + 2] = 1.0;
        idx += 1;
      }
      for (const p of nearbyPts) {
        pos[idx * 3 + 0] = p.x;
        pos[idx * 3 + 1] = p.y;
        pos[idx * 3 + 2] = p.z;
        col[idx * 3 + 0] = 0.35;
        col[idx * 3 + 1] = 1.0;
        col[idx * 3 + 2] = 0.35;
        idx += 1;
      }

      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      this.dragDebugPoints = new THREE.Points(g, this.dragDebugPointsMat);
      (this.dragDebugPoints as any).renderOrder = 1001;
      this.scene.add(this.dragDebugPoints);
    }

    if (solved.debug.chosenPair && this.dragDebugLineMat) {
      const g = new THREE.BufferGeometry().setFromPoints([
        solved.debug.chosenPair.aWorld,
        solved.debug.chosenPair.bWorld,
      ]);
      this.dragDebugPairLine = new THREE.Line(g, this.dragDebugLineMat);
      (this.dragDebugPairLine as any).renderOrder = 1002;
      this.scene.add(this.dragDebugPairLine);
    }

    if (this.dragGhostBoundsHelper) {
      this.dragGhostBoundsBox.setFromObject(obj);
      this.dragGhostBoundsHelper.box.copy(this.dragGhostBoundsBox);
      this.dragGhostBoundsHelper.updateMatrixWorld(true);
      this.dragGhostBoundsHelper.visible = true;
    }
  }

  private logChosenSnapDebug(solved: DragSolveResult, finalized: boolean) {
    if (!import.meta.env.DEV) return;

    const p = solved.debug.chosenPair;
    if (!p) {
      if (finalized) console.debug(`[snap] finalSource=${solved.debug.finalSource} (no snap pair)`);
      this.lastSnapDebugSignature = null;
      return;
    }

    const sig =
      `${solved.debug.finalSource}|${p.aId}|${p.bId}|` +
      `${p.deltaLen.toFixed(6)}|${p.postSnapDeltaY.toFixed(6)}|${solved.finalPose.position.y.toFixed(5)}|` +
      `${finalized ? 'final' : 'preview'}`;
    if (!finalized && sig === this.lastSnapDebugSignature) return;
    this.lastSnapDebugSignature = sig;

    const mw = p.maleWorld;
    const fw = p.femaleWorld;
    const d = p.delta;
    console.debug(
      `[snap:${finalized ? 'final' : 'preview'}] finalSource=${solved.debug.finalSource} ` +
      `pair a=${p.aId} b=${p.bId} male=${p.maleId} female=${p.femaleId} ` +
      `malePos=(${mw.x.toFixed(4)}, ${mw.y.toFixed(4)}, ${mw.z.toFixed(4)}) ` +
      `femalePos=(${fw.x.toFixed(4)}, ${fw.y.toFixed(4)}, ${fw.z.toFixed(4)}) ` +
      `preSnapDeltaY=${p.preSnapDeltaY.toFixed(6)} postSnapDeltaY=${p.postSnapDeltaY.toFixed(6)} ` +
      `penetrationDepth=${p.maxPenetrationDepth.toFixed(6)} raise=${p.raiseAmount.toFixed(6)} ` +
      `delta=(${d.x.toFixed(6)}, ${d.y.toFixed(6)}, ${d.z.toFixed(6)}) |delta|=${p.deltaLen.toExponential(3)}`
    );
  }

  private clearDragDebug() {
    if (this.dragDebugPoints) {
      this.scene.remove(this.dragDebugPoints);
      this.dragDebugPoints.geometry.dispose();
      this.dragDebugPoints = null;
    }
    if (this.dragDebugPairLine) {
      this.scene.remove(this.dragDebugPairLine);
      this.dragDebugPairLine.geometry.dispose();
      this.dragDebugPairLine = null;
    }
    if (this.dragGhostBoundsHelper) {
      this.dragGhostBoundsHelper.visible = false;
    }
    this.lastSnapDebugSignature = null;
  }

  private getOtherLeafParts(selectedObj: THREE.Object3D): THREE.Object3D[] {
    // All visible part objects excluding selected subtree (works for parts + instance groups).
    const out: THREE.Object3D[] = [];
    for (const p of this.leafParts) {
      if (!p.visible) continue;
      if (p === selectedObj) continue;
      // Exclude descendants of selected object
      let cur: THREE.Object3D | null = p;
      let isDesc = false;
      while (cur) {
        if (cur === selectedObj) {
          isDesc = true;
          break;
        }
        cur = cur.parent;
      }
      if (isDesc) continue;
      out.push(p);
    }
    return out;
  }

  dispose() {
    cancelAnimationFrame(this.animationHandle);
    this.disposeFns.forEach((f) => f());
    this.finishFreeDrag(true);
    this.setPlacementDraft(null);
    this.clearDragDebug();
    this.dragSolveState = null;
    if (this.dragGhostBoundsHelper) {
      this.scene.remove(this.dragGhostBoundsHelper);
      (this.dragGhostBoundsHelper.geometry as any)?.dispose?.();
      (this.dragGhostBoundsHelper.material as any)?.dispose?.();
      this.dragGhostBoundsHelper = null;
    }
    this.dragDebugPointsMat?.dispose();
    this.dragDebugLineMat?.dispose();
    this.dragDebugPointsMat = null;
    this.dragDebugLineMat = null;
    if (this.instanceBoundsHelper) {
      this.scene.remove(this.instanceBoundsHelper);
      (this.instanceBoundsHelper.geometry as any)?.dispose?.();
      (this.instanceBoundsHelper.material as any)?.dispose?.();
      this.instanceBoundsHelper = null;
    }
    this.controls.dispose();
    this.transform.dispose();
    if (this.transformHelper) this.scene.remove(this.transformHelper);
    this.composer.dispose();
    this.renderer.dispose();
    this.host.removeChild(this.renderer.domElement);
  }
}
